import { z } from "zod";
import {
	BLOCK_INTERACTION_HOLD_MS,
	estimateTextMs,
} from "../../timeline/reading-rate";
import {
	asRecord,
	blockProvenanceHintSchema,
	compact,
	emptyBlockSettingsSchema,
	pickProvenance,
	str,
	TOPIC_BLOCK_INSTRUCTION_TEMPLATES,
} from "../content-helpers";
import {
	ALL_BLOCK_PATCH_OPS,
	defineBlockKind,
	STRUCTURED_BLOCK_PATCH_OPS,
} from "../define-block-kind";
import { ATOM_BLOCK_DOC_NODE } from "../doc-node";

export const dividerBlockContentSchema = z
	.object({
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type DividerBlockContent = z.infer<typeof dividerBlockContentSchema>;

export const dividerBlockKind = defineBlockKind({
	type: "DIVIDER",
	contentSchema: dividerBlockContentSchema,
	defaultContent: {},
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "stackable",
	textWeight: 0,
	normalizeContent: (raw): DividerBlockContent =>
		compact({ ...pickProvenance(raw) }),
	deriveNarrationText: () => "",
	estimateDwellMs: () => 0,
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});

export const processBlockStepSchema = z
	.object({
		title: z.string(),
		body: z.string(),
	})
	.strict();

export const PROCESS_CARD_LAYOUTS = ["combined", "oneStepPerCard"] as const;

export const processBlockContentSchema = z
	.object({
		title: z.string().nullable(),
		steps: z.array(processBlockStepSchema).min(1),
		cardLayout: z.enum(PROCESS_CARD_LAYOUTS),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type ProcessBlockContent = z.infer<typeof processBlockContentSchema>;
export type ProcessBlockStep = z.infer<typeof processBlockStepSchema>;

export const processBlockKind = defineBlockKind({
	type: "PROCESS",
	contentSchema: processBlockContentSchema,
	defaultContent: {
		title: null,
		steps: [{ title: "", body: "" }],
		cardLayout: "combined",
	},
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "process",
	textWeight: 0,
	normalizeContent: (raw): ProcessBlockContent => {
		const steps = (Array.isArray(raw.steps) ? raw.steps : [])
			.map((step) => {
				const row = asRecord(step);
				return { title: str(row.title), body: str(row.body) };
			})
			.filter((step) => step.title.length > 0 || step.body.length > 0);
		return compact({
			title: str(raw.title) || null,
			steps: steps.length > 0 ? steps : [{ title: "", body: "" }],
			cardLayout:
				raw.cardLayout === "oneStepPerCard"
					? "oneStepPerCard"
					: "combined",
			...pickProvenance(raw),
		});
	},
	// Steps are spoken by title (unchanged from the pre-registry narration).
	deriveNarrationText: (content) =>
		(Array.isArray(content.steps) ? content.steps : [])
			.map((step) => str(asRecord(step).title))
			.filter((text) => text.length > 0)
			.join("\n"),
	estimateDwellMs: (content) =>
		estimateTextMs(
			[
				str(content.title),
				...(Array.isArray(content.steps) ? content.steps : []).flatMap(
					(step) => [
						str(asRecord(step).title),
						str(asRecord(step).body),
					],
				),
			].join("\n"),
		),
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});

export function parseProcessBlockContent(raw: unknown): ProcessBlockContent {
	return processBlockContentSchema.parse(raw);
}

/** A QUIZ block's questions (P14d, D53): one is its own card, 2–5 are a carousel in one card. */
export const QUIZ_BLOCK_MAX_QUESTIONS = 5;

export const quizBlockContentSchema = z
	.object({
		/** Questions in carousel order; empty while generation has not assigned them yet. */
		quizQuestionIds: z
			.array(z.string().min(1))
			.max(QUIZ_BLOCK_MAX_QUESTIONS)
			.refine((ids) => new Set(ids).size === ids.length, {
				message: "A QUIZ block lists each question once",
			}),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type QuizBlockContent = z.infer<typeof quizBlockContentSchema>;

export function parseQuizBlockContent(raw: unknown): QuizBlockContent {
	return quizBlockContentSchema.parse(raw);
}

/** The block's question ids, in order, each once, capped at `QUIZ_BLOCK_MAX_QUESTIONS`. */
export function readQuizQuestionIds(content: unknown): string[] {
	const raw = asRecord(content).quizQuestionIds;
	const ids: string[] = [];
	for (const value of Array.isArray(raw) ? raw : []) {
		const id = typeof value === "string" ? value.trim() : "";
		if (id && !ids.includes(id)) {
			ids.push(id);
		}
	}
	return ids.slice(0, QUIZ_BLOCK_MAX_QUESTIONS);
}

/** Assembled QUIZ blocks carry the hydrated `questions` (not persisted), in block order. */
function quizNarrationText(content: Record<string, unknown>): string {
	const parts: string[] = [TOPIC_BLOCK_INSTRUCTION_TEMPLATES.QUIZ];
	for (const entry of Array.isArray(content.questions)
		? content.questions
		: []) {
		const question = asRecord(entry);
		const questionText = str(question.questionText);
		const answers = (
			Array.isArray(question.answers) ? question.answers : []
		)
			.map((answer) => {
				const row = asRecord(answer);
				return str(row.answerText) || str(row.text);
			})
			.filter((text) => text.length > 0);
		if (questionText) {
			parts.push(questionText);
		}
		if (answers.length > 0) {
			parts.push(answers.join("\n"));
		}
	}
	return parts.join("\n\n");
}

export const quizBlockKind = defineBlockKind({
	type: "QUIZ",
	contentSchema: quizBlockContentSchema,
	defaultContent: { quizQuestionIds: [] },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: STRUCTURED_BLOCK_PATCH_OPS,
	docNode: ATOM_BLOCK_DOC_NODE,
	defaultSettings: {},
	partitionRole: "full_card",
	textWeight: 0,
	normalizeContent: (raw): QuizBlockContent =>
		compact({
			quizQuestionIds: readQuizQuestionIds(raw),
			...pickProvenance(raw),
		}),
	deriveNarrationText: quizNarrationText,
	/** Reads the hydrated questions and answers, then holds for each answer. */
	estimateDwellMs: (content) =>
		estimateTextMs(quizNarrationText(content)) +
		BLOCK_INTERACTION_HOLD_MS.QUIZ_PER_QUESTION *
			Math.max(1, readQuizQuestionIds(content).length),
	/** Every question of the block is answered before its card completes (D53). */
	gate: (content) =>
		readQuizQuestionIds(content).map((questionId) => ({
			kind: "quiz.answer" as const,
			questionId,
		})),
	interactions: [],
	exportPolicy: { pdf: "question_and_options" },
});
